import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2, Paperclip, ChevronDown, ChevronUp, Search, X, Download, StickyNote } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { apiClient, setAuthToken, formatApiError } from "@/lib/api";
import { NewsManager } from "@/components/admin/NewsManager";
import { SocialLinksManager } from "@/components/admin/SocialLinksManager";
import { KnowledgeBaseManager } from "@/components/admin/KnowledgeBaseManager";
import { ChatSessionsManager } from "@/components/admin/ChatSessionsManager";
import { InvoiceManager } from "@/components/admin/InvoiceManager";
import { ContractManager } from "@/components/admin/ContractManager";
import { ShipmentManager } from "@/components/admin/ShipmentManager";
import logo from "@/assets/logo.png";
import { toast } from "sonner";

const VIEWS = [
  { id: "submissions", label: "Follow-Up Queue" },
  { id: "invoices", label: "Invoices" },
  { id: "contracts", label: "Contracts" },
  { id: "shipments", label: "Shipments" },
  { id: "news", label: "News & Media" },
  { id: "social", label: "Social Links" },
  { id: "knowledge", label: "Knowledge Base" },
  { id: "conversations", label: "Conversations" },
];

const TABS = [
  { id: "all", label: "All" },
  { id: "quote", label: "Quote Requests" },
  { id: "filing", label: "Customs Filings" },
  { id: "contact", label: "Contact" },
];

const STATUS_OPTIONS = ["New", "In Review", "Responded", "Closed"];

const STATUS_STYLES = {
  New: "bg-gold/20 text-gold",
  "In Review": "bg-blue-500/20 text-blue-300",
  Responded: "bg-emerald-500/20 text-emerald-300",
  Closed: "bg-[#6E7E74]/25 text-[#A1B0A6]",
};

const typeLabel = { quote: "Quote Request", filing: "Customs Filing", contact: "Contact" };

function csvEscape(val) {
  const s = String(val ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

export default function AdminDashboard() {
  const [view, setView] = useState("submissions");
  const [tab, setTab] = useState("all");
  const [submissions, setSubmissions] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [refSearch, setRefSearch] = useState("");
  const [drafts, setDrafts] = useState({});
  const navigate = useNavigate();

  const fetchSubmissions = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/admin/submissions");
      setSubmissions(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ferguson_admin_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not load submissions");
      }
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("ferguson_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    setAuthToken(token);
    fetchSubmissions();
  }, [navigate, fetchSubmissions]);

  const handleLogout = () => {
    localStorage.removeItem("ferguson_admin_token");
    setAuthToken(null);
    navigate("/admin/login");
  };

  const getDraft = (item, key) => drafts[key] || { status: item.status, note: "" };

  const setDraftField = (key, item, field, value) =>
    setDrafts((d) => ({ ...d, [key]: { ...getDraft(item, key), [field]: value } }));

  const saveStatus = async (item, key) => {
    const draft = getDraft(item, key);
    try {
      await apiClient.patch(`/admin/submissions/${item.type}/${item.id}`, {
        status: draft.status,
        note: draft.note.trim() || undefined,
      });
      toast.success("Status updated");
      setDrafts((d) => ({ ...d, [key]: { status: draft.status, note: "" } }));
      fetchSubmissions();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update status");
    }
  };

  const downloadFile = async (item) => {
    try {
      const { data } = await apiClient.get(`/admin/files/${item.file_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.file_original_name || "attachment";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download file");
    }
  };

  const filtered = submissions
    ? submissions
        .filter((s) => tab === "all" || s.type === tab)
        .filter((s) =>
          refSearch.trim()
            ? (s.reference_number || "").toUpperCase().includes(refSearch.trim().toUpperCase())
            : true
        )
    : null;

  const openCount = submissions ? submissions.filter((s) => s.status !== "Closed").length : 0;

  const exportCSV = () => {
    if (!filtered || filtered.length === 0) return;
    const headers = [
      "Type", "Reference Number", "Name / Company", "Email", "Phone",
      "Origin", "Destination", "Cargo Type / Direction", "Country",
      "Shipment Date", "Status", "Submitted At", "Internal Notes",
    ];
    const rows = filtered.map((item) => {
      const notes = (item.internal_notes || [])
        .map((n) => `[${n.status}] ${n.text} (${new Date(n.created_at).toLocaleString()})`)
        .join(" | ");
      return [
        typeLabel[item.type] || item.type,
        item.reference_number || "",
        item.company_name || item.full_name || "",
        item.email || "",
        item.phone || "",
        item.origin_country || "",
        item.destination_country || "",
        item.cargo_type || item.shipment_direction || "",
        item.country || "",
        item.estimated_shipment_date || "",
        item.status || "",
        item.created_at || "",
        notes,
      ];
    });
    const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fergusons-submissions-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} submission${filtered.length === 1 ? "" : "s"}`);
  };

  const primaryLabel = (item) => item.company_name || item.full_name || "—";
  const secondaryLabel = (item) => item.email || item.phone || "";

  return (
    <div className="min-h-screen bg-deep" data-testid="admin-dashboard-container">
      <header className="border-b border-gold/15 bg-deep-surface">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Ferguson's logo" className="h-9 w-auto" />
            <span className="font-display font-semibold text-[#F4F7F5]">Admin Dashboard</span>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-sm text-[#A1B0A6] hover:text-gold transition-colors"
          >
            <LogOut size={16} /> Log Out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-6">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              data-testid={`admin-view-${v.id}`}
              onClick={() => setView(v.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                view === v.id ? "bg-gold text-[#0A140F]" : "bg-deep-card text-[#A1B0A6] hover:text-gold"
              }`}
            >
              {v.label}
              {v.id === "submissions" && openCount > 0 && (
                <span
                  data-testid="admin-followup-open-count-badge"
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    view === v.id ? "bg-[#0A140F] text-gold" : "bg-gold/20 text-gold"
                  }`}
                >
                  {openCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {view === "news" ? (
          <NewsManager />
        ) : view === "social" ? (
          <SocialLinksManager />
        ) : view === "knowledge" ? (
          <KnowledgeBaseManager />
        ) : view === "conversations" ? (
          <ChatSessionsManager />
        ) : view === "invoices" ? (
          <InvoiceManager />
        ) : view === "contracts" ? (
          <ContractManager />
        ) : view === "shipments" ? (
          <ShipmentManager />
        ) : (
        <>
        <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
          <h2 className="font-display font-bold text-xl text-[#F4F7F5]" data-testid="admin-followup-queue-heading">
            Follow-Up Queue
            <span className="ml-2 text-sm font-medium text-[#A1B0A6]" data-testid="admin-followup-open-count-text">
              ({openCount} open)
            </span>
          </h2>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  tab === t.id ? "bg-gold text-[#0A140F]" : "bg-deep-card text-[#A1B0A6] hover:text-gold"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7E74]" />
              <input
                value={refSearch}
                onChange={(e) => setRefSearch(e.target.value)}
                placeholder="Search by reference number"
                data-testid="admin-reference-search-input"
                className="w-full bg-deep-card border border-gold/15 rounded-full pl-9 pr-8 py-2 text-sm text-[#F4F7F5] placeholder:text-[#6E7E74] focus:outline-none focus:border-gold/50"
              />
              {refSearch && (
                <button
                  onClick={() => setRefSearch("")}
                  data-testid="admin-reference-search-clear-btn"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E7E74] hover:text-gold"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              onClick={exportCSV}
              disabled={!filtered || filtered.length === 0}
              data-testid="admin-export-csv-btn"
              className="shrink-0 flex items-center gap-1.5 text-sm font-medium bg-gold hover:bg-gold-hover disabled:opacity-40 text-[#0A140F] rounded-full px-4 py-2 transition-colors"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        {!filtered ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-gold" size={28} />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-[#A1B0A6] text-center py-20" data-testid="admin-no-submissions">
            {refSearch ? "No submissions match that reference number." : "No submissions yet."}
          </p>
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => {
              const key = `${item.type}-${item.id}`;
              const isOpen = expanded === key;
              const draft = getDraft(item, key);
              const notes = item.internal_notes || [];
              return (
                <div
                  key={key}
                  data-testid={`admin-submission-row-${key}`}
                  className="rounded-xl border border-gold/15 bg-deep-card overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 text-left"
                    onClick={() => setExpanded(isOpen ? null : key)}
                    data-testid={`admin-submission-toggle-${key}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-gold/10 text-gold shrink-0">
                        {typeLabel[item.type]}
                      </span>
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-[#F4F7F5] truncate">{primaryLabel(item)}</div>
                        <div className="text-xs text-[#A1B0A6] truncate">{secondaryLabel(item)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {item.reference_number && (
                        <span className="text-xs font-mono text-[#6E7E74] hidden sm:inline">
                          {item.reference_number}
                        </span>
                      )}
                      {notes.length > 0 && (
                        <span className="flex items-center gap-1 text-xs text-[#6E7E74]" title={`${notes.length} note(s)`}>
                          <StickyNote size={13} /> {notes.length}
                        </span>
                      )}
                      <span className="text-xs text-[#6E7E74] hidden sm:inline">
                        {new Date(item.created_at).toLocaleString()}
                      </span>
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLES[item.status]}`}>
                        {item.status}
                      </span>
                      {isOpen ? <ChevronUp size={16} className="text-[#A1B0A6]" /> : <ChevronDown size={16} className="text-[#A1B0A6]" />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-gold/10 p-4 sm:p-5 bg-deep/60">
                      <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        {Object.entries(item)
                          .filter(([k]) => !["id", "type", "status", "file_path", "file_content_type", "internal_notes"].includes(k))
                          .map(([k, v]) => (
                            <div key={k}>
                              <div className="text-[#6E7E74] text-xs uppercase tracking-wide">{k.replace(/_/g, " ")}</div>
                              <div className="text-[#D7E0DA] break-words">{String(v || "—")}</div>
                            </div>
                          ))}
                      </div>

                      {item.file_path && (
                        <button
                          onClick={() => downloadFile(item)}
                          data-testid={`admin-download-file-${key}`}
                          className="mt-4 flex items-center gap-2 text-sm text-gold hover:text-gold-hover transition-colors"
                        >
                          <Paperclip size={15} /> Download {item.file_original_name}
                        </button>
                      )}

                      <div className="mt-5 pt-4 border-t border-gold/10">
                        <div className="text-[#6E7E74] text-xs uppercase tracking-wide mb-1.5">Update Status</div>
                        <div className="flex flex-wrap items-start gap-3">
                          <Select value={draft.status} onValueChange={(v) => setDraftField(key, item, "status", v)}>
                            <SelectTrigger data-testid={`admin-status-select-${key}`} className="w-48">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s}>
                                  {s}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Textarea
                            value={draft.note}
                            onChange={(e) => setDraftField(key, item, "note", e.target.value)}
                            placeholder="Internal note (visible only in this dashboard)..."
                            data-testid={`admin-note-input-${key}`}
                            rows={1}
                            className="flex-1 min-w-[200px] bg-deep border-gold/15 text-[#F4F7F5] placeholder:text-[#6E7E74]"
                          />
                          <button
                            onClick={() => saveStatus(item, key)}
                            disabled={draft.status === item.status && !draft.note.trim()}
                            data-testid={`admin-save-status-btn-${key}`}
                            className="bg-gold hover:bg-gold-hover disabled:opacity-40 text-[#0A140F] text-sm font-display font-semibold px-5 py-2 rounded-md transition-colors"
                          >
                            Save
                          </button>
                        </div>
                      </div>

                      {notes.length > 0 && (
                        <div className="mt-4">
                          <div className="text-[#6E7E74] text-xs uppercase tracking-wide mb-2">Note History</div>
                          <div className="space-y-2" data-testid={`admin-notes-list-${key}`}>
                            {notes
                              .slice()
                              .reverse()
                              .map((n, i) => (
                                <div key={i} className="rounded-lg bg-deep border border-gold/10 p-3 text-sm">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] font-mono uppercase text-gold">{n.status}</span>
                                    <span className="text-[10px] text-[#6E7E74]">
                                      {new Date(n.created_at).toLocaleString()}
                                    </span>
                                  </div>
                                  <p className="text-[#D7E0DA]">{n.text}</p>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
